import { createId, nowIso } from "../lib/ids.ts";
import {
  MAX_IMAGE_BYTES,
  MAX_IMAGE_PIXELS,
  ROOM_IMAGE_SLOTS,
  type AssetRecord,
  type RecoveryJournalEntry,
} from "../lib/types.ts";
import * as db from "./indexeddb.ts";
import * as opfs from "./opfs.ts";

const MAX_DISPLAY = 1600;
const MAX_THUMB = 256;
const MAX_TRANSPARENCY_INSPECTION = 1600;

export interface ImportPhotoFileOptions {
  allowedMimeTypes?: readonly string[];
  requireTransparency?: boolean;
}

interface PreparedImage {
  image: HTMLImageElement;
  sha256: string;
}

async function sha256(blob: Blob): Promise<string> {
  const buffer = await blob.arrayBuffer();
  const hash = await crypto.subtle.digest("SHA-256", buffer);
  return [...new Uint8Array(hash)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

function loadImage(blob: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(blob);
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to decode image"));
    };
    img.src = url;
  });
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Canvas export failed"))),
      type,
      quality,
    );
  });
}

async function resizeImage(
  source: HTMLImageElement,
  maxSize: number,
  mimeType: string,
  preserveTransparency = false,
): Promise<{ blob: Blob; width: number; height: number }> {
  const scale = Math.min(1, maxSize / Math.max(source.width, source.height));
  const width = Math.max(1, Math.round(source.width * scale));
  const height = Math.max(1, Math.round(source.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas unavailable");
  ctx.drawImage(source, 0, 0, width, height);
  const outputType = mimeType === "image/png" || preserveTransparency ? "image/png" : "image/jpeg";
  const blob = await canvasToBlob(canvas, outputType, 0.9);
  return { blob, width, height };
}

function mimeTypeLabel(mimeType: string): string {
  switch (mimeType.toLowerCase()) {
    case "image/jpeg":
      return "JPEG";
    case "image/png":
      return "PNG";
    case "image/webp":
      return "WebP";
    default:
      return mimeType.split("/").at(-1)?.toUpperCase() || mimeType;
  }
}

function validateMimeType(file: File, options: ImportPhotoFileOptions): void {
  const mimeType = file.type.toLowerCase();
  const allowedMimeTypes = options.allowedMimeTypes?.map((allowed) => allowed.toLowerCase());
  if (allowedMimeTypes && !allowedMimeTypes.includes(mimeType)) {
    const labels = allowedMimeTypes.map(mimeTypeLabel);
    const supported = labels.length > 0 ? labels.join(" or ") : "the configured";
    throw new Error(`Only ${supported} image types are supported`);
  }
  if (!mimeType.startsWith("image/")) {
    throw new Error("Only image files are supported");
  }
}

function hasTransparency(source: HTMLImageElement): boolean {
  const scale = Math.min(
    1,
    MAX_TRANSPARENCY_INSPECTION / Math.max(source.width, source.height),
  );
  const width = Math.max(1, Math.round(source.width * scale));
  const height = Math.max(1, Math.round(source.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) throw new Error("Canvas unavailable");
  ctx.drawImage(source, 0, 0, width, height);

  const rowsPerChunk = Math.max(1, Math.floor(1_000_000 / width));
  for (let y = 0; y < height; y += rowsPerChunk) {
    const chunkHeight = Math.min(rowsPerChunk, height - y);
    const pixels = ctx.getImageData(0, y, width, chunkHeight).data;
    for (let index = 3; index < pixels.length; index += 4) {
      if (pixels[index] !== 255) return true;
    }
  }
  return false;
}

async function prepareImage(file: Blob, requireTransparency: boolean): Promise<PreparedImage> {
  if (file.size > MAX_IMAGE_BYTES) {
    throw new Error(`Image exceeds ${Math.round(MAX_IMAGE_BYTES / (1024 * 1024))}MB limit`);
  }
  const image = await loadImage(file);
  if (image.width <= 0 || image.height <= 0) {
    throw new Error("Image has invalid dimensions");
  }
  if (image.width * image.height > MAX_IMAGE_PIXELS) {
    throw new Error("Image resolution is too large");
  }
  if (requireTransparency && !hasTransparency(image)) {
    throw new Error("Image must contain transparent pixels");
  }
  return { image, sha256: await sha256(file) };
}

async function collectReferencedAssetIds(): Promise<Set<string>> {
  // Read protection before durable refs. Once a journal entry is absent, its
  // reference was committed first, so the later album/settings reads see it.
  const journal = await db.listJournal();
  const albums = await db.listAlbums();
  const settings = await db.getSettings();
  const ids = new Set<string>();
  for (const entry of journal) ids.add(entry.assetId);
  for (const album of albums) {
    if (album.coverAssetId) ids.add(album.coverAssetId);
    for (const page of album.pages) {
      for (const placement of page.placements) ids.add(placement.assetId);
    }
  }
  for (const slot of ROOM_IMAGE_SLOTS) {
    const assetId = settings?.roomImages?.[slot];
    if (assetId) ids.add(assetId);
  }
  return ids;
}

export async function garbageCollectAssets(): Promise<void> {
  // Snapshot assets before refs so an import cannot become collectible between
  // its journal write and its ready asset write.
  const assets = await db.listAssets();
  const referenced = await collectReferencedAssetIds();
  for (const asset of assets) {
    if (asset.status !== "ready") continue;
    if (referenced.has(asset.id)) continue;
    for (const variant of Object.values(asset.variants)) {
      opfs.revokeObjectUrl(variant.opfsPath);
    }
    await opfs.deleteAssetDir(asset.id);
    await db.deleteAsset(asset.id);
  }
}

function importJournalKey(assetId: string): string {
  return `import-${assetId}`;
}

/** Release import protection after a durable asset reference has been saved. */
export async function finalizeImportedAsset(assetId: string): Promise<void> {
  await db.deleteJournal(importJournalKey(assetId));
}

/** Release a failed handoff and immediately reclaim the now-unreferenced asset. */
export async function discardImportedAsset(assetId: string): Promise<void> {
  await db.deleteJournal(importJournalKey(assetId));
  await garbageCollectAssets();
}

export async function createAssetFromBlob(
  assetId: string,
  file: Blob,
  mimeType: string,
  description: string | null = null,
): Promise<AssetRecord> {
  const prepared = await prepareImage(file, false);
  return createAssetFromPreparedImage(assetId, file, mimeType, description, prepared, false);
}

async function createAssetFromPreparedImage(
  assetId: string,
  file: Blob,
  mimeType: string,
  description: string | null,
  prepared: PreparedImage,
  preserveTransparency: boolean,
): Promise<AssetRecord> {
  const { image } = prepared;
  const display = await resizeImage(image, MAX_DISPLAY, mimeType, preserveTransparency);
  const thumb = await resizeImage(image, MAX_THUMB, mimeType, preserveTransparency);

  const originalPath = opfs.assetPath(assetId, "original");
  const displayPath = opfs.assetPath(assetId, "display");
  const thumbnailPath = opfs.assetPath(assetId, "thumbnail");

  await opfs.writeBlob(originalPath, file);
  await opfs.writeBlob(displayPath, display.blob);
  await opfs.writeBlob(thumbnailPath, thumb.blob);

  const originalCheck = await opfs.readBlob(originalPath);
  const displayCheck = await opfs.readBlob(displayPath);
  const thumbCheck = await opfs.readBlob(thumbnailPath);
  if (!originalCheck || !displayCheck || !thumbCheck) {
    throw new Error("Failed to verify imported image variants");
  }

  return {
    id: assetId,
    mimeType,
    width: image.width,
    height: image.height,
    sha256: prepared.sha256,
    description,
    variants: {
      original: { opfsPath: originalPath, size: file.size },
      display: { opfsPath: displayPath, size: display.blob.size },
      thumbnail: { opfsPath: thumbnailPath, size: thumb.blob.size },
    },
    createdAt: nowIso(),
    status: "ready",
  };
}

export async function importPhotoFile(
  file: File,
  options: ImportPhotoFileOptions = {},
): Promise<AssetRecord> {
  validateMimeType(file, options);
  const requireTransparency = options.requireTransparency ?? false;
  const prepared = await prepareImage(file, requireTransparency);

  const assetId = createId();
  const journalKey = importJournalKey(assetId);
  const originalPath = opfs.assetPath(assetId, "original");
  const journalEntry: RecoveryJournalEntry = {
    key: journalKey,
    assetId,
    variant: "original",
    opfsPath: originalPath,
    createdAt: nowIso(),
  };
  const pending: AssetRecord = {
    id: assetId,
    mimeType: file.type,
    width: 0,
    height: 0,
    sha256: "",
    description: null,
    variants: {
      original: { opfsPath: originalPath, size: file.size },
      display: { opfsPath: opfs.assetPath(assetId, "display"), size: 0 },
      thumbnail: { opfsPath: opfs.assetPath(assetId, "thumbnail"), size: 0 },
    },
    createdAt: nowIso(),
    status: "pending",
  };
  await db.putJournal(journalEntry);

  try {
    await db.putAsset(pending);
    const ready = await createAssetFromPreparedImage(
      assetId,
      file,
      file.type,
      null,
      prepared,
      requireTransparency,
    );
    await db.putAsset(ready);
    return ready;
  } catch (error) {
    await Promise.allSettled([
      opfs.deleteAssetDir(assetId),
      db.deleteAsset(assetId),
      db.deleteJournal(journalKey),
    ]);
    throw error;
  }
}

export async function reconcilePendingAssets(): Promise<void> {
  const assets = await db.listAssets();
  for (const asset of assets) {
    if (asset.status === "pending") {
      const original = await opfs.readBlob(asset.variants.original.opfsPath);
      const display = await opfs.readBlob(asset.variants.display.opfsPath);
      const thumb = await opfs.readBlob(asset.variants.thumbnail.opfsPath);
      if (original && display && thumb) {
        await db.putAsset({ ...asset, status: "ready" });
      } else {
        await opfs.deleteAssetDir(asset.id);
        await db.deleteAsset(asset.id);
      }
      continue;
    }
    if (asset.status === "failed") {
      await opfs.deleteAssetDir(asset.id);
      await db.deleteAsset(asset.id);
    }
  }

  const journal = await db.listJournal();
  for (const entry of journal) {
    const blob = await opfs.readBlob(entry.opfsPath);
    const record = await db.getAsset(entry.assetId);
    if (blob && record?.status === "ready") {
      await db.deleteJournal(entry.key);
      continue;
    }
    if (!blob || !record || record.status !== "ready") {
      await opfs.deletePath(entry.opfsPath);
      await db.deleteJournal(entry.key);
      if (record && record.status !== "ready") {
        await opfs.deleteAssetDir(entry.assetId);
        await db.deleteAsset(entry.assetId);
      }
    }
  }
}
